import type { CardData } from '@gachakit/core/types/card';

export interface FriendPackDefinition {
  id: string;
  name: string;
  description: string;
  cards: CardData[];
}

const PONTUS_IMAGE = '/friends/pontus-hellzen.png';
const PONTUS_FUJI_IMAGE = '/friends/pontus-fuji-correspondent.webp';
const PONTUS_NEON_IMAGE = '/friends/pontus-neon-sources.webp';
const PONTUS_HEADSHOT_IMAGE = '/friends/pontus-editorial-headshot.webp';
const JERRY_IMAGE = '/friends/jerry-bergman.webp';
const DAVID_IMAGE = '/friends/david-forslund.webp';
const DALAN_IMAGE = '/friends/dalan-berserker.webp';
const ALEX_IMAGE = '/friends/alex-liu-chef.webp';
const EXO_IMAGE = '/friends/christoffer-exo-dahlin.webp';
const MAWE_IMAGE = '/friends/mawe-chaos.webp';
const ANDREAS_IMAGE = '/friends/andreas-landin.webp';
const EMIL_IMAGE = '/friends/emil-eriksson.webp';
const HENRIK_LOCAL_IMAGE = '/friends/henrik-solin-local-gangster.webp';
const HENRIK_FISHERMAN_IMAGE = '/friends/henrik-solin-fisherman.webp';
const HENRIK_SUPPORT_IMAGE = '/friends/henrik-solin-it-support.webp';
const ANDREAS_KARLSSON_TRAV_IMAGE = '/friends/andreas-karlsson-travkung.webp';
const ANDREAS_KARLSSON_FISHING_IMAGE = '/friends/andreas-karlsson-fishing.webp';
const RASMUS_IMAGE = '/friends/rasmus-smedberg-hockeylegend.webp';
const NICKLAS_IMAGE = '/friends/nicklas-grasse-grahn-timra-picasso.webp';
const JERRY_THIMMY_COMBO_IMAGE = '/friends/jerry-thimmy-family-combo-wombo.webp';
const SEBASTIAN_DALAN_IMAGE = '/friends/sebastian-dalan-dahlqvist-tea-guitar.webp';
const TED_IMAGE = '/friends/ted-dahlberg-rockstar.webp';

export const friendPacks: FriendPackDefinition[] = [
  {
    id: 'ai-press-pack',
    name: 'AI Press Pack',
    description: 'Pontus Hellzen variants for anyone who thinks the newsroom needs more tokens.',
    cards: [
      {
        id: 'pontus-prompt-desk',
        name: 'Pontus Hellzen',
        subtitle: 'Prompt Desk Reporter',
        description: 'AI scoop. Clean copy.',
        imageUrl: PONTUS_IMAGE,
        rarity: 'common',
        tags: ['pontus', 'ai', 'journalist', 'prompting'],
        stats: [
          { label: 'Role', value: 'Reporter', highlight: true },
          { label: 'Beat', value: 'AI' }
        ],
        metadata: {
          cardCode: 'PH-001',
          typeLabel: 'AI',
          hp: '110',
          spellName: 'Prompt Blast',
          spellCost: 2,
          spellPower: '30',
          flavor: 'Turns prompts into headlines before coffee cools.',
          weakness: 'Typo x2',
          resistance: 'Hype -20',
          retreat: '1 edit',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'prompt-desk'
        }
      },
      {
        id: 'pontus-source-whisperer',
        name: 'Pontus Hellzen',
        subtitle: 'Source Whisperer',
        description: 'Cites receipts. Wins trust.',
        imageUrl: PONTUS_IMAGE,
        rarity: 'uncommon',
        tags: ['pontus', 'ai', 'journalist', 'sources'],
        stats: [
          { label: 'Role', value: 'Verifier', highlight: true },
          { label: 'Beat', value: 'Sources' }
        ],
        metadata: {
          cardCode: 'PH-002',
          typeLabel: 'Press',
          hp: '125',
          spellName: 'Cite Sources',
          spellCost: 2,
          spellPower: '40',
          flavor: 'The chatbot apologizes and adds footnotes.',
          weakness: 'Rumor x2',
          resistance: 'Spin -20',
          retreat: '1 quote',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'source-whisperer'
        }
      },
      {
        id: 'pontus-algorithm-columnist',
        name: 'Pontus Hellzen',
        subtitle: 'Algorithm Columnist',
        description: 'Explains models. No jargon.',
        imageUrl: PONTUS_IMAGE,
        rarity: 'rare',
        tags: ['pontus', 'ai', 'journalist', 'columnist'],
        stats: [
          { label: 'Role', value: 'Columnist', highlight: true },
          { label: 'Beat', value: 'Models' }
        ],
        metadata: {
          cardCode: 'PH-003',
          typeLabel: 'AI',
          hp: '145',
          spellName: 'Explain Patch',
          spellCost: 3,
          spellPower: '60',
          flavor: 'Dense update in. Human sentence out.',
          weakness: 'Acronym x2',
          resistance: 'Buzz -30',
          retreat: '2 drafts',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'algorithm-columnist'
        }
      },
      {
        id: 'pontus-embargo-breaker',
        name: 'Pontus Hellzen',
        subtitle: 'Embargo Breaker',
        description: 'Drops scoop. Inbox burns.',
        imageUrl: PONTUS_IMAGE,
        rarity: 'epic',
        tags: ['pontus', 'ai', 'journalist', 'scoop'],
        stats: [
          { label: 'Role', value: 'Scoop', highlight: true },
          { label: 'Beat', value: 'Startups' }
        ],
        metadata: {
          cardCode: 'PH-004',
          typeLabel: 'Press',
          hp: '170',
          spellName: 'Scoop Drop',
          spellCost: 4,
          spellPower: '90',
          flavor: 'Three leaks before breakfast.',
          weakness: 'Embargo x2',
          resistance: 'PR -30',
          retreat: '2 calls',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'embargo-breaker'
        }
      },
      {
        id: 'pontus-sentient-byline',
        name: 'Pontus Hellzen',
        subtitle: 'Sentient Byline',
        description: 'Files once. Editors bow.',
        imageUrl: PONTUS_IMAGE,
        rarity: 'legendary',
        tags: ['pontus', 'ai', 'journalist', 'legend'],
        stats: [
          { label: 'Role', value: 'Byline', highlight: true },
          { label: 'Beat', value: 'Future' }
        ],
        metadata: {
          cardCode: 'PH-005',
          typeLabel: 'AI',
          hp: '220',
          spellName: 'Final Draft',
          spellCost: 5,
          spellPower: '130',
          flavor: 'Passed the editor and the AI detector.',
          weakness: 'Deadline x2',
          resistance: 'Rewrite -40',
          retreat: '3 tabs',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'sentient-byline'
        }
      },
      {
        id: 'pontus-fuji-field-reporter',
        name: 'Pontus Hellzen',
        subtitle: 'Fuji Field Reporter',
        description: 'Scenic lead. Sharp angle.',
        imageUrl: PONTUS_FUJI_IMAGE,
        rarity: 'rare',
        tags: ['pontus', 'ai', 'journalist', 'travel'],
        stats: [
          { label: 'Role', value: 'Field', highlight: true },
          { label: 'Beat', value: 'Fuji' }
        ],
        metadata: {
          cardCode: 'PH-006',
          typeLabel: 'Field',
          hp: '150',
          spellName: 'Dateline Fuji',
          spellCost: 3,
          spellPower: '70',
          flavor: 'Even the mountain gives a quote.',
          weakness: 'Cloud x2',
          resistance: 'Lag -20',
          retreat: '2 trains',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'fuji-field-reporter'
        }
      },
      {
        id: 'pontus-neon-source-check',
        name: 'Pontus Hellzen',
        subtitle: 'Neon Source Check',
        description: 'Night quote. Clean notes.',
        imageUrl: PONTUS_NEON_IMAGE,
        rarity: 'epic',
        tags: ['pontus', 'ai', 'journalist', 'nightlife'],
        stats: [
          { label: 'Role', value: 'Interview', highlight: true },
          { label: 'Beat', value: 'Neon' }
        ],
        metadata: {
          cardCode: 'PH-007',
          typeLabel: 'Press',
          hp: '180',
          spellName: 'Neon Quote',
          spellCost: 4,
          spellPower: '95',
          flavor: 'Recorder on. Vibes immaculate.',
          weakness: 'Noise x2',
          resistance: 'Doubt -30',
          retreat: '2 cans',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'neon-source-check'
        }
      },
      {
        id: 'pontus-editorial-overlord',
        name: 'Pontus Hellzen',
        subtitle: 'Editorial Overlord',
        description: 'Fixes intro. Saves story.',
        imageUrl: PONTUS_HEADSHOT_IMAGE,
        rarity: 'legendary',
        tags: ['pontus', 'ai', 'journalist', 'editorial'],
        stats: [
          { label: 'Role', value: 'Editor', highlight: true },
          { label: 'Beat', value: 'Clarity' }
        ],
        metadata: {
          cardCode: 'PH-008',
          typeLabel: 'Press',
          hp: '240',
          spellName: 'Rewrite Intro',
          spellCost: 5,
          spellPower: '140',
          flavor: 'The first paragraph never stood a chance.',
          weakness: 'Ramble x2',
          resistance: 'Slop -40',
          retreat: '3 edits',
          packId: 'ai-press-pack',
          characterName: 'Pontus Hellzen',
          variant: 'editorial-overlord'
        }
      }
    ]
  },
  {
    id: 'azeroth-raid-pack',
    name: 'Azeroth Raid Pack',
    description: 'Jerry Bergman World of Warcraft superstar cards for raid-night glory.',
    cards: [
      {
        id: 'jerry-tavern-hero',
        name: 'Jerry Bergman',
        subtitle: 'Tavern Hero',
        description: 'Buffs up. Logs in.',
        imageUrl: JERRY_IMAGE,
        rarity: 'common',
        tags: ['jerry', 'world of warcraft', 'wow', 'tavern'],
        stats: [
          { label: 'Class', value: 'Hero', highlight: true },
          { label: 'Zone', value: 'Tavern' }
        ],
        metadata: {
          cardCode: 'JB-001',
          typeLabel: 'WoW',
          hp: '120',
          spellName: 'Well Fed Buff',
          spellCost: 2,
          spellPower: '30',
          flavor: 'Ready check passes instantly.',
          weakness: 'AFK x2',
          resistance: 'Lag -20',
          retreat: '1 hearth',
          packId: 'azeroth-raid-pack',
          characterName: 'Jerry Bergman',
          variant: 'tavern-hero'
        }
      },
      {
        id: 'jerry-mythic-grinder',
        name: 'Jerry Bergman',
        subtitle: 'Mythic Grinder',
        description: 'One more key. Always.',
        imageUrl: JERRY_IMAGE,
        rarity: 'uncommon',
        tags: ['jerry', 'world of warcraft', 'wow', 'mythic'],
        stats: [
          { label: 'Class', value: 'Grinder', highlight: true },
          { label: 'Zone', value: 'Dungeon' }
        ],
        metadata: {
          cardCode: 'JB-002',
          typeLabel: 'WoW',
          hp: '140',
          spellName: 'Key Push',
          spellCost: 3,
          spellPower: '55',
          flavor: 'The timer fears him.',
          weakness: 'Pug x2',
          resistance: 'Wipe -20',
          retreat: '2 portals',
          packId: 'azeroth-raid-pack',
          characterName: 'Jerry Bergman',
          variant: 'mythic-grinder'
        }
      },
      {
        id: 'jerry-loot-master',
        name: 'Jerry Bergman',
        subtitle: 'Loot Master',
        description: 'Needs once. Wins twice.',
        imageUrl: JERRY_IMAGE,
        rarity: 'rare',
        tags: ['jerry', 'world of warcraft', 'wow', 'loot'],
        stats: [
          { label: 'Class', value: 'Loot', highlight: true },
          { label: 'Zone', value: 'Vault' }
        ],
        metadata: {
          cardCode: 'JB-003',
          typeLabel: 'WoW',
          hp: '165',
          spellName: 'Need Roll',
          spellCost: 3,
          spellPower: '75',
          flavor: 'Best in slot, best in smile.',
          weakness: 'Greed x2',
          resistance: 'Vendor -30',
          retreat: '2 rolls',
          packId: 'azeroth-raid-pack',
          characterName: 'Jerry Bergman',
          variant: 'loot-master'
        }
      },
      {
        id: 'jerry-raid-caller',
        name: 'Jerry Bergman',
        subtitle: 'Raid Caller',
        description: 'Counts down. Team obeys.',
        imageUrl: JERRY_IMAGE,
        rarity: 'epic',
        tags: ['jerry', 'world of warcraft', 'wow', 'raid'],
        stats: [
          { label: 'Class', value: 'Caller', highlight: true },
          { label: 'Zone', value: 'Raid' }
        ],
        metadata: {
          cardCode: 'JB-004',
          typeLabel: 'WoW',
          hp: '190',
          spellName: 'Pull Timer',
          spellCost: 4,
          spellPower: '100',
          flavor: 'Five, four, three, delete boss.',
          weakness: 'Mute x2',
          resistance: 'Chaos -30',
          retreat: '3 pings',
          packId: 'azeroth-raid-pack',
          characterName: 'Jerry Bergman',
          variant: 'raid-caller'
        }
      },
      {
        id: 'jerry-azeroth-superstar',
        name: 'Jerry Bergman',
        subtitle: 'Azeroth Superstar',
        description: 'Top meters. No mercy.',
        imageUrl: JERRY_IMAGE,
        rarity: 'legendary',
        tags: ['jerry', 'world of warcraft', 'wow', 'legend'],
        stats: [
          { label: 'Class', value: 'Superstar', highlight: true },
          { label: 'Zone', value: 'Azeroth' }
        ],
        metadata: {
          cardCode: 'JB-005',
          typeLabel: 'WoW',
          hp: '250',
          spellName: 'Raid Carry',
          spellCost: 5,
          spellPower: '150',
          flavor: 'The DPS meter needs its own monitor.',
          weakness: 'Patch day x2',
          resistance: 'Nerf -40',
          retreat: '4 hearths',
          packId: 'azeroth-raid-pack',
          characterName: 'Jerry Bergman',
          variant: 'azeroth-superstar'
        }
      }
    ]
  },
  {
    id: 'superstar-pack',
    name: 'Superstar Pack',
    description: 'Political stars, berserkers, chefs, tacticians, all-rounders, local fixers, trav kings, hockey legends, painters, family combos, tea experts, rockstars, hobby generals, and consequence-free chaos.',
    cards: [
      {
        id: 'david-forslund-political-superstar',
        name: 'David Forslund',
        subtitle: 'Political Superstar',
        description: 'Wins room. Owns podium.',
        imageUrl: DAVID_IMAGE,
        rarity: 'legendary',
        tags: ['david', 'politics', 'superstar', 'podium'],
        stats: [
          { label: 'Role', value: 'Statesman', highlight: true },
          { label: 'Arena', value: 'Podium' }
        ],
        metadata: {
          cardCode: 'DF-001',
          typeLabel: 'Pol',
          hp: '235',
          spellName: 'Vote Surge',
          spellCost: 5,
          spellPower: '140',
          flavor: 'Handshake, headline, landslide.',
          weakness: 'Poll dip x2',
          resistance: 'Spin -40',
          retreat: '3 debates',
          packId: 'superstar-pack',
          characterName: 'David Forslund',
          variant: 'political-superstar'
        }
      },
      {
        id: 'dalan-helmet-berserker',
        name: 'Dalan',
        subtitle: 'Helmet Berserker',
        description: 'Charges first. Asks later.',
        imageUrl: DALAN_IMAGE,
        rarity: 'epic',
        tags: ['dalan', 'berserker', 'chaos', 'helmet'],
        stats: [
          { label: 'Role', value: 'Berserker', highlight: true },
          { label: 'Mode', value: 'Full send' }
        ],
        metadata: {
          cardCode: 'DL-001',
          typeLabel: 'Rage',
          hp: '195',
          spellName: 'Helmet Rush',
          spellCost: 4,
          spellPower: '105',
          flavor: 'Safety optional. Sunglasses mandatory.',
          weakness: 'Plan x2',
          resistance: 'Fear -30',
          retreat: '2 brakes',
          packId: 'superstar-pack',
          characterName: 'Dalan',
          variant: 'helmet-berserker'
        }
      },
      {
        id: 'alex-liu-line-cook',
        name: 'Alex Liu',
        subtitle: 'Superstar Chef',
        description: 'Pan hot. Plate clean.',
        imageUrl: ALEX_IMAGE,
        rarity: 'rare',
        tags: ['alex', 'chef', 'kitchen', 'superstar'],
        stats: [
          { label: 'Role', value: 'Chef', highlight: true },
          { label: 'Station', value: 'Pass' }
        ],
        metadata: {
          cardCode: 'AL-001',
          typeLabel: 'Chef',
          hp: '160',
          spellName: 'Sear Finish',
          spellCost: 3,
          spellPower: '75',
          flavor: 'Quiet focus. Loud flavor.',
          weakness: 'Overcook x2',
          resistance: 'Rush -20',
          retreat: '2 plates',
          packId: 'superstar-pack',
          characterName: 'Alex Liu',
          variant: 'line-cook'
        }
      },
      {
        id: 'chauwhauw-legendary-service',
        name: 'ChauwHauw',
        subtitle: 'Legendary Chef',
        description: 'Final dish. Table silent.',
        imageUrl: ALEX_IMAGE,
        rarity: 'legendary',
        tags: ['chauwhauw', 'chef', 'legend', 'service'],
        stats: [
          { label: 'Role', value: 'Legend', highlight: true },
          { label: 'Station', value: 'Chef' }
        ],
        metadata: {
          cardCode: 'CH-001',
          typeLabel: 'Chef',
          hp: '245',
          spellName: 'Perfect Service',
          spellCost: 5,
          spellPower: '150',
          flavor: 'Only legendary cards may say the name.',
          weakness: 'Burnt sauce x2',
          resistance: 'Stress -40',
          retreat: '4 courses',
          finish: 'holo',
          packId: 'superstar-pack',
          characterName: 'ChauwHauw',
          variant: 'legendary-service'
        }
      },
      {
        id: 'christoffer-exo-dahlin-grand-tactician',
        name: 'Christoffer "Exo" Dahlin',
        subtitle: 'Grand Tactician',
        description: 'Sees all. Wins before move one.',
        imageUrl: EXO_IMAGE,
        rarity: 'legendary',
        tags: ['exo', 'tactician', 'wisdom', 'holo'],
        stats: [
          { label: 'Role', value: 'Tactician', highlight: true },
          { label: 'Mind', value: 'Planetary' }
        ],
        metadata: {
          cardCode: 'EX-001',
          typeLabel: 'Mind',
          hp: '300',
          spellName: 'Perfect Read',
          spellCost: 5,
          spellPower: '180',
          flavor: 'Best tactician. Wisest mind. No contest.',
          weakness: 'None',
          resistance: 'Everything -50',
          retreat: '0 mistakes',
          finish: 'reverse-holo',
          packId: 'superstar-pack',
          characterName: 'Christoffer Dahlin',
          variant: 'grand-tactician'
        }
      },
      {
        id: 'mawe-consequence-free',
        name: 'Mawe',
        subtitle: 'Consequence-Free Chaos',
        description: 'No plan. Full commit.',
        imageUrl: MAWE_IMAGE,
        rarity: 'epic',
        tags: ['mawe', 'tavern', 'chaos', 'party'],
        stats: [
          { label: 'Role', value: 'Chaos', highlight: true },
          { label: 'Sense', value: 'Offline' }
        ],
        metadata: {
          cardCode: 'MW-001',
          typeLabel: 'Wild',
          hp: '205',
          spellName: 'One More Round',
          spellCost: 4,
          spellPower: '120',
          flavor: 'Does the thing. Reads consequences later.',
          weakness: 'Tomorrow x2',
          resistance: 'Regret -30',
          retreat: '0 brakes',
          packId: 'superstar-pack',
          characterName: 'Mawe',
          variant: 'consequence-free'
        }
      },
      {
        id: 'andreas-landin-bekab-battle-trainer',
        name: 'Andreas Landin',
        subtitle: 'Bekab Battle Trainer',
        description: 'Builds by day. Battles by night.',
        imageUrl: ANDREAS_IMAGE,
        rarity: 'epic',
        tags: ['andreas', 'landin', 'bekab', 'pokemon', 'warhammer'],
        stats: [
          { label: 'Work', value: 'Bekab', highlight: true },
          { label: 'Hobby', value: 'Pokemon & Warhammer' }
        ],
        metadata: {
          cardCode: 'AND-001',
          typeLabel: 'Hobby',
          hp: '190',
          spellName: 'Dice & Decks',
          spellCost: 4,
          spellPower: '105',
          flavor: 'Paints the army, sleeves the deck, wins the table.',
          weakness: 'Rules Lookup x2',
          resistance: 'Meta -30',
          retreat: '2 dice',
          packId: 'superstar-pack',
          characterName: 'Andreas Landin',
          variant: 'bekab-battle-trainer'
        }
      },
      {
        id: 'emil-eriksson-skoter-all-rounder',
        name: 'Emil Eriksson',
        subtitle: 'Skoter All-Rounder',
        description: 'Fixes anything. Rides harder.',
        imageUrl: EMIL_IMAGE,
        rarity: 'epic',
        tags: ['emil', 'eriksson', 'allrounder', 'skoter', 'snus'],
        stats: [
          { label: 'Role', value: 'All-rounder', highlight: true },
          { label: 'Ride', value: 'Skoter' }
        ],
        metadata: {
          cardCode: 'EE-001',
          typeLabel: 'Skoter',
          hp: '200',
          spellName: 'Full Throttle',
          spellCost: 4,
          spellPower: '115',
          flavor: 'One hand on the throttle, one dosa in reserve.',
          weakness: 'Empty Dosa x2',
          resistance: 'Cold -30',
          retreat: '2 tracks',
          packId: 'superstar-pack',
          characterName: 'Emil Eriksson',
          variant: 'skoter-all-rounder'
        }
      },
      {
        id: 'henrik-solin-local-gangster',
        name: 'Henrik Solin',
        subtitle: 'Local Gangster',
        description: 'Knows people. Keeps order.',
        imageUrl: HENRIK_LOCAL_IMAGE,
        rarity: 'rare',
        tags: ['henrik', 'solin', 'local', 'gangster', 'fixer'],
        stats: [
          { label: 'Role', value: 'Local Fixer', highlight: true },
          { label: 'Turf', value: 'Neighborhood' }
        ],
        metadata: {
          cardCode: 'HS-001',
          typeLabel: 'Local',
          hp: '170',
          spellName: 'Street Cred',
          spellCost: 3,
          spellPower: '80',
          flavor: 'The whole block knows the support ticket is handled.',
          weakness: 'Paper Trail x2',
          resistance: 'Drama -30',
          retreat: '2 favors',
          packId: 'superstar-pack',
          characterName: 'Henrik Solin',
          variant: 'local-gangster'
        }
      },
      {
        id: 'henrik-solin-it-support',
        name: 'Henrik Solin',
        subtitle: 'IT Support Fixer',
        description: 'Clicks once. Problem gone.',
        imageUrl: HENRIK_SUPPORT_IMAGE,
        rarity: 'uncommon',
        tags: ['henrik', 'solin', 'it-support', 'support', 'fixer'],
        stats: [
          { label: 'Role', value: 'IT Support', highlight: true },
          { label: 'Ticket', value: 'Resolved' }
        ],
        metadata: {
          cardCode: 'HS-002',
          typeLabel: 'Tech',
          hp: '145',
          spellName: 'Reboot Check',
          spellCost: 2,
          spellPower: '45',
          flavor: 'Local network bends to his will.',
          weakness: 'Printer x2',
          resistance: 'User Error -20',
          retreat: '1 cable',
          packId: 'superstar-pack',
          characterName: 'Henrik Solin',
          variant: 'it-support'
        }
      },
      {
        id: 'henrik-solin-fisher-homeowner',
        name: 'Henrik Solin',
        subtitle: 'Fisher Homeowner',
        description: 'Owns house. Hooks dinner.',
        imageUrl: HENRIK_FISHERMAN_IMAGE,
        rarity: 'epic',
        tags: ['henrik', 'solin', 'fishing', 'homeowner', 'house'],
        stats: [
          { label: 'Property', value: 'House', highlight: true },
          { label: 'Hobby', value: 'Fishing' }
        ],
        metadata: {
          cardCode: 'HS-003',
          typeLabel: 'Home',
          hp: '215',
          spellName: 'Mortgage Cast',
          spellCost: 4,
          spellPower: '115',
          flavor: 'Owns the roof, the rod, and the evening plan.',
          weakness: 'Rain x2',
          resistance: 'Rent -30',
          retreat: '2 lures',
          packId: 'superstar-pack',
          characterName: 'Henrik Solin',
          variant: 'fisher-homeowner'
        }
      },
      {
        id: 'andreas-karlsson-travkung-teacher',
        name: 'Andreas Karlsson',
        subtitle: 'Travkung Teacher',
        description: 'Teaches class. Picks winners.',
        imageUrl: ANDREAS_KARLSSON_TRAV_IMAGE,
        rarity: 'legendary',
        tags: ['andreas', 'karlsson', 'trav', 'teacher', 'school'],
        stats: [
          { label: 'Role', value: 'School Teacher', highlight: true },
          { label: 'Track', value: 'Trav King' }
        ],
        metadata: {
          cardCode: 'AK-001',
          typeLabel: 'Trav',
          hp: '230',
          spellName: 'Final Odds',
          spellCost: 5,
          spellPower: '145',
          flavor: 'Lesson plan in one hand, winning slip in the other.',
          weakness: 'Late Bell x2',
          resistance: 'Bad Odds -40',
          retreat: '3 laps',
          packId: 'superstar-pack',
          characterName: 'Andreas Karlsson',
          variant: 'travkung-teacher'
        }
      },
      {
        id: 'andreas-karlsson-fish-and-sprit',
        name: 'Andreas Karlsson',
        subtitle: 'Fish & Sprit Specialist',
        description: 'Casts line. Pours steady.',
        imageUrl: ANDREAS_KARLSSON_FISHING_IMAGE,
        rarity: 'epic',
        tags: ['andreas', 'karlsson', 'fishing', 'sprit', 'teacher'],
        stats: [
          { label: 'Hobby', value: 'Fishing', highlight: true },
          { label: 'Fuel', value: 'Sprit' }
        ],
        metadata: {
          cardCode: 'AK-002',
          typeLabel: 'Fish',
          hp: '205',
          spellName: 'Catch & Cheers',
          spellCost: 4,
          spellPower: '120',
          flavor: 'Knows every fish, every bottle, and every homework excuse.',
          weakness: 'Dry Glass x2',
          resistance: 'Cold Water -30',
          retreat: '2 lures',
          packId: 'superstar-pack',
          characterName: 'Andreas Karlsson',
          variant: 'fish-and-sprit'
        }
      },
      {
        id: 'rasmus-smedberg-hockeylegend',
        name: 'Rasmus Smedberg',
        subtitle: 'Hockeylegend',
        description: 'Shoots hard. Boards suffer.',
        imageUrl: RASMUS_IMAGE,
        rarity: 'legendary',
        tags: ['rasmus', 'smedberg', 'hockey', 'legend', 'snus'],
        stats: [
          { label: 'Role', value: 'Hockey Legend', highlight: true },
          { label: 'Shot', value: 'Board Breaker' }
        ],
        metadata: {
          cardCode: 'RS-001',
          typeLabel: 'Hockey',
          hp: '240',
          spellName: 'Sargkross',
          spellCost: 5,
          spellPower: '155',
          flavor: 'One slapshot, one broken board, one fresh dosa.',
          weakness: 'Empty Dosa x2',
          resistance: 'Checking -40',
          retreat: '3 shifts',
          packId: 'superstar-pack',
          characterName: 'Rasmus Smedberg',
          variant: 'hockeylegend'
        }
      },
      {
        id: 'nicklas-grasse-grahn-timra-picasso',
        name: 'Nicklas "Grasse" Grahn',
        subtitle: 'Timra Picasso',
        description: 'Clocks in. Paints legend.',
        imageUrl: NICKLAS_IMAGE,
        rarity: 'legendary',
        tags: ['nicklas', 'grasse', 'grahn', 'timra', 'maleri', 'painter'],
        stats: [
          { label: 'Work', value: 'Timra Maleri', highlight: true },
          { label: 'Title', value: 'Timra Picasso' }
        ],
        metadata: {
          cardCode: 'NG-001',
          typeLabel: 'Paint',
          hp: '235',
          spellName: 'Perfect Coat',
          spellCost: 5,
          spellPower: '150',
          flavor: 'Every wall gets a signature finish.',
          weakness: 'Rain x2',
          resistance: 'Drips -40',
          retreat: '3 ladders',
          packId: 'superstar-pack',
          characterName: 'Nicklas Grahn',
          variant: 'timra-picasso'
        }
      },
      {
        id: 'jerry-bergman-thimmy-wallgren-family-combo-wombo',
        name: 'Jerry Bergman & Thimmy Wallgren',
        subtitle: 'Family Combo Wombo',
        description: 'Family stack. Combo lands.',
        imageUrl: JERRY_THIMMY_COMBO_IMAGE,
        rarity: 'legendary',
        tags: ['jerry', 'bergman', 'thimmy', 'wallgren', 'family', 'combo'],
        stats: [
          { label: 'Duo', value: 'Family Combo', highlight: true },
          { label: 'Move', value: 'Combo Wombo' }
        ],
        metadata: {
          cardCode: 'JT-001',
          typeLabel: 'Combo',
          hp: '260',
          spellName: 'Family Combo Wombo',
          spellCost: 5,
          spellPower: '170',
          flavor: 'One photo turns into a full-board family buff.',
          weakness: 'Solo Queue x2',
          resistance: 'Tilt -40',
          retreat: '3 hugs',
          finish: 'holo',
          packId: 'superstar-pack',
          characterName: 'Jerry Bergman & Thimmy Wallgren',
          variant: 'family-combo-wombo'
        }
      },
      {
        id: 'sebastian-dalan-dahlqvist-tea-guitar-expert',
        name: 'Sebastian Dalan Dahlqvist',
        subtitle: 'Tea & Guitar Expert',
        description: 'Steeps deep. Shreds clean.',
        imageUrl: SEBASTIAN_DALAN_IMAGE,
        rarity: 'epic',
        tags: ['sebastian', 'dalan', 'dahlqvist', 'tea', 'guitar'],
        stats: [
          { label: 'Brew', value: 'Tea Expert', highlight: true },
          { label: 'Instrument', value: 'Guitar' }
        ],
        metadata: {
          cardCode: 'SDD-001',
          typeLabel: 'Tea',
          hp: '205',
          spellName: 'Steep Solo',
          spellCost: 4,
          spellPower: '115',
          flavor: 'Finds the perfect chord while the kettle does the warmup.',
          weakness: 'Broken String x2',
          resistance: 'Bitterness -30',
          retreat: '2 picks',
          packId: 'superstar-pack',
          characterName: 'Sebastian Dalan Dahlqvist',
          variant: 'tea-guitar-expert'
        }
      },
      {
        id: 'ted-dahlberg-rockstar',
        name: 'Ted Dahlberg',
        subtitle: 'Rockstar',
        description: 'Takes stage. Owns crowd.',
        imageUrl: TED_IMAGE,
        rarity: 'legendary',
        tags: ['ted', 'dahlberg', 'rockstar', 'music', 'stage'],
        stats: [
          { label: 'Role', value: 'Rockstar', highlight: true },
          { label: 'Stage', value: 'Main Act' }
        ],
        metadata: {
          cardCode: 'TD-001',
          typeLabel: 'Rock',
          hp: '245',
          spellName: 'Encore Drop',
          spellCost: 5,
          spellPower: '155',
          flavor: 'Walks in like the headliner, leaves like the legend.',
          weakness: 'Feedback x2',
          resistance: 'Stage Fright -40',
          retreat: '3 amps',
          packId: 'superstar-pack',
          characterName: 'Ted Dahlberg',
          variant: 'rockstar'
        }
      }
    ]
  }
];

export const allFriendCards = friendPacks.flatMap((pack) => pack.cards);
